import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit2, Plus, Trash2 } from "lucide-react";
import AppModal from "../../components/AppModal";
import DataTable from "../../components/DataTable";
import ResourcePage from "../../components/ResourcePage";
import {
  createCompanyUser,
  deleteCompanyUser,
  fetchCompanyUsers,
  updateCompanyUser,
} from "../../redux/userSlice";

const emptyForm = {
  nome: "",
  email: "",
  telefone: "",
  nivel: "caixa",
  status: "activo",
  senha: "",
};

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const showModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).show();
};

const Users = () => {
  const dispatch = useDispatch();
  const { users, loading, error, user } = useSelector((state) => state.user);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    dispatch(fetchCompanyUsers());
  }, [dispatch]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      nome: item.nome || "",
      email: item.email || "",
      telefone: item.telefone || "",
      nivel: item.nivel || "caixa",
      status: item.status || "activo",
      senha: "",
    });
    showModal("userFormModal");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = { ...form };
    if (editing && !payload.senha) delete payload.senha;

    const result = editing
      ? await dispatch(updateCompanyUser({ id: editing.id, data: payload }))
      : await dispatch(createCompanyUser(payload));

    if (createCompanyUser.fulfilled.match(result) || updateCompanyUser.fulfilled.match(result)) {
      setForm(emptyForm);
      setEditing(null);
      closeModal("userFormModal");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const result = await dispatch(deleteCompanyUser(deleting.id));
    if (deleteCompanyUser.fulfilled.match(result)) {
      setDeleting(null);
      closeModal("deleteUserModal");
    }
  };

  const canManage = user?.nivel === "admin";

  return (
    <ResourcePage
      title="Utilizadores"
      breadcrumb={["Definições", "Utilizadores"]}
      actions={(
        <>
          <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#userFormModal" onClick={openCreate} disabled={!canManage}>
            <Plus size={16} />
            Cadastrar
          </button>
        </>
      )}
    >
      <div className="card app-card">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Apenas administradores podem gerir utilizadores.</div>}

          <DataTable
            id="usersDataTable"
            columns={["Nome", "Email", "Telefone", "Nível", "Estado", "Ações"]}
            rows={users}
            renderRow={(item) => (
              <tr key={item.id}>
                <td>{item.nome}</td>
                <td>{item.email}</td>
                <td>{item.telefone || "-"}</td>
                <td><span className="badge bg-primary-subtle text-primary">{item.nivel}</span></td>
                <td><span className={`badge ${item.status === "activo" ? "bg-success-subtle text-success" : "bg-secondary-subtle text-secondary"}`}>{item.status}</span></td>
                <td>
                  <div className="d-flex justify-content-end gap-1">
                    <button className="btn btn-light btn-sm btn-icon" type="button" onClick={() => openEdit(item)} disabled={!canManage} title="Editar">
                      <Edit2 size={15} />
                    </button>
                    <button className="btn btn-light text-danger btn-sm btn-icon" type="button" onClick={() => { setDeleting(item); showModal("deleteUserModal"); }} disabled={!canManage || item.id === user?.id} title="Excluir">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            )}
          />
        </div>
      </div>

      <AppModal
        id="userFormModal"
        title={editing ? "Editar utilizador" : "Cadastrar utilizador"}
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="userForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="userForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="nome">Nome</label>
              <input className="form-control" id="nome" name="nome" value={form.nome} onChange={handleChange} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="email">Email</label>
              <input className="form-control" id="email" name="email" type="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label" htmlFor="telefone">Telefone</label>
              <input className="form-control" id="telefone" name="telefone" value={form.telefone} onChange={handleChange} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label" htmlFor="nivel">Nível</label>
              <select className="form-select" id="nivel" name="nivel" value={form.nivel} onChange={handleChange}>
                <option value="caixa">Caixa</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label" htmlFor="status">Estado</label>
              <select className="form-select" id="status" name="status" value={form.status} onChange={handleChange}>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </select>
            </div>
            <div className="col-12">
              <label className="form-label" htmlFor="senha">Senha</label>
              <input className="form-control" id="senha" name="senha" type="password" value={form.senha} onChange={handleChange} required={!editing} placeholder={editing ? "Preencha apenas se quiser alterar a senha" : ""} />
            </div>
          </div>
        </form>
      </AppModal>

      <AppModal
        id="deleteUserModal"
        title="Excluir utilizador"
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={loading}>
              {loading ? "A excluir..." : "Excluir"}
            </button>
          </>
        )}
      >
        <p className="mb-0">Deseja excluir o utilizador <strong>{deleting?.nome}</strong>?</p>
      </AppModal>
    </ResourcePage>
  );
};

export default Users;
